import { useState } from 'react';
import { AdFormData, MarketFormData, CampaignFormData } from '../types';
import { useAppDispatch } from './useAppDipatch';
import { createCampaign } from '../features/campaigns/campaignsThunk';
import { createMarket } from '../features/markets/marketsThunk';
import { createAd } from '../features/ads/adsThunk';
import { useAppSelector } from './useAppSelector';
import { setCreationStep, setShowLaunchConfirmed } from '../features/campaigns/campaignsSlice';
import { MarketPayload } from '../api/controllers/market.controller';
import { CampaignController } from '../api/controllers/campaign.controller';

export interface MarketSubmitResult {
  success?: boolean;
  needsBilling?: boolean;
}

export const useCampaignsManager = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { market } = useAppSelector((state) => state.markets);
  const { campaign } = useAppSelector((state) => state.campaigns);
  const [pendingCampaign, setPendingCampaign] = useState<CampaignFormData | null>(null);
  const [pendingMarket, setPendingMarket] = useState<MarketFormData | null>(null);
  const [showBillingModal, setShowBillingModal] = useState<boolean>(false);
  const [pendingMarketData, setPendingMarketData] = useState<MarketFormData | null>(null);

  const handleCampaignSubmit = async (data: CampaignFormData, retainerFile?: File | null) => {
    try {
      const params = {
        name: data.name,
        objective: data.objective,
        claim_type: data.claim_type,
        daily_budget: data.daily_budget,
        destination_phone_number: data.destination_phone_number,
        total_budget: data.total_budget === "" ? "0" : data.total_budget,
        start_at: data.start_at!,
        end_at: data.end_at,
        start_time: data.start_time,
        end_time: data.end_time,
        timezone: data.timezone,
        advertiser_id: user.id,
      };

      const campaign = await dispatch(createCampaign(params));
      if (campaign) {
        setPendingCampaign({
          ...data,
          id: campaign.id,
        });

        // Upload retainer document after campaign creation
        if (retainerFile && data.objective.toLowerCase() === 'signed_retainers') {
          await CampaignController.uploadRetainerDocument(campaign.id, retainerFile);
        }
      }
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const handleMarketSubmit = async (data: MarketFormData): Promise<MarketSubmitResult> => {
    try {
      if (!campaign || !campaign.id) return { success: false };

      // Check if user needs to set up billing before launching
      if (user?.billing_status === 'inactive') {
        setPendingMarketData(data);
        setShowBillingModal(true);
        return { needsBilling: true };
      }

      const status = data.activateImmediately === false ? 'paused' : 'active';

      const params: MarketPayload = {
        campaign_id: data.campaignID ?? campaign.id,
        name: data.name,
        targeting: data.targeting,
        call_hours: data.callHours,
        parent_bid: data.parentBid,
        subtype_bids: data.subtypeBids,
        status: status,
      };

      const newMarket = await dispatch(createMarket(params));
      if (newMarket) {
        setPendingMarket(data);
        dispatch(setCreationStep('none'));
        // Always show launch confirmation modal
        dispatch(setShowLaunchConfirmed(true));
        return { success: true };
      }
      return { success: false };
    } catch (err) {
      console.error(err);
      return { success: false };
    }
  };

  const closeBillingModal = () => {
    setShowBillingModal(false);
    setPendingMarketData(null);
  };

  const handleAdSubmit = async (data: AdFormData) => {
    try {
      if (!market || !market.id) return;

      const params = {
        adset_id: market.id,
        name: data.name,
        questions: data.leadForm?.questions ?? [],
      };

      const newAd = await dispatch(createAd(params));
      if (newAd) dispatch(setCreationStep('none'));
    } catch (err) {
      console.error(err);
    }
  };

  return {
    pendingCampaign,
    pendingMarket,
    pendingMarketData,
    showBillingModal,
    handleAdSubmit,
    handleMarketSubmit,
    handleCampaignSubmit,
    closeBillingModal,
  };
};
